import {
  assoc,
  concat,
  curry,
  find,
  map,
  pick,
  reduce,
  values,
  view
} from "ramda";
import {
  AccountDescription,
  AccountId,
  SpreadFinancialValue
} from "../spread/types";
import {
  AccountDescriptions,
  getAccountDescription
} from "./accountDescriptions";
import {
  AccountFormula,
  AccountFormulas,
  getAccountFormula
} from "./accountFormulas";
import { AccountHierarchy, getChildren } from "./accountHierarchy";
import collectFormulaSummedValues from "./formulas/collectFormulaSummedValues";
import {
  mapReduceSpreadFinancialValues,
  SpreadFinancialValueLens,
  SpreadFinancialValueNode,
  SpreadFinancialValues,
  SpreadFinancialValuesStatus
} from "./spreadFinancialValues";
import { AccountMap } from "./types";

export interface SummedValue {
  spreadFinancialValueLens: SpreadFinancialValueLens;
  spreadFinancialValue: SpreadFinancialValue;
  accountId: AccountId;
}

export interface AccountValue {
  value: number | undefined;
  summedValues: SummedValue[];
}

export type AccountValueMap = AccountMap<AccountValue>;

export type AccountValues = AccountValueMap[];

const getChildValues = curry(
  (childIds: AccountId[], accountValueMap: AccountValueMap): AccountValue[] =>
    values(pick(childIds, accountValueMap))
);

export const periodSum = reduce<AccountValue, AccountValue>(
  (total, periodValue) =>
    periodValue.value === undefined
      ? total
      : {
          value: periodValue.value + (total.value || 0),
          summedValues: concat(total.summedValues, periodValue.summedValues)
        },
  { value: undefined, summedValues: [] }
);

export const financialValueValue = (
  financialValue: SpreadFinancialValue | undefined
) => {
  if (financialValue) {
    switch (financialValue.valueType) {
      case "DOUBLE":
      case "INTEGER":
        return financialValue.userEditedValue
          ? Number(financialValue.adjustedValue) || undefined
          : financialValue.value || undefined;
      default:
        return undefined;
    }
  } else {
    return undefined;
  }
};

export const getSummedValues = (
  accountFormula: AccountFormula,
  accountValueMap: AccountValueMap,
  childIds: AccountId[]
): SummedValue[] =>
  accountFormula
    ? collectFormulaSummedValues(accountFormula, accountValueMap)
    : periodSum(getChildValues(childIds, accountValueMap)).summedValues;

export const getAccountValue = (
  accountId: AccountId,
  accountDescription: AccountDescription,
  spreadFinancialValue: SpreadFinancialValue,
  spreadFinancialValueLens: SpreadFinancialValueLens,
  accountFormula: AccountFormula,
  childrenIds: AccountId[],
  accountValueMap: AccountValueMap
): AccountValue => {
  const summedValues = getSummedValues(
    accountFormula,
    accountValueMap,
    childrenIds
  );

  if (accountDescription.type === "CALCULATED") {
    if (spreadFinancialValue.value) {
      return {
        value: Number(spreadFinancialValue.value),
        summedValues
      };
    }

    return { value: undefined, summedValues: [] };
  }

  if (spreadFinancialValue.userEditedValue) {
    if (
      accountDescription.type === "HIDDEN" &&
      spreadFinancialValue.hasAdjustedValue
    ) {
      return {
        value: Number(spreadFinancialValue.adjustedValue) || undefined, // Hidden accounts should not show value 0, but rather be empty
        summedValues: [
          {
            accountId,
            spreadFinancialValue,
            spreadFinancialValueLens
          }
        ]
      };
    }

    return {
      value: financialValueValue(spreadFinancialValue),
      summedValues
    };
  }

  // NOTE: This was removed during the mapping epic to prevent hiding hidden accounts in the COA
  // if (
  //   spreadFinancialValue.hasModelDerivedValue &&
  //   spreadFinancialValue.modelDerivedValue
  // ) {
  //   return {
  //     value: Number(spreadFinancialValue.modelDerivedValue),
  //     summedValues: [
  //       {
  //         accountId,
  //         spreadFinancialValue,
  //         spreadFinancialValueLens
  //       }
  //     ]
  //   };
  // }

  if (summedValues.length > 0 && !spreadFinancialValue.userEditedValue) {
    return {
      value: financialValueValue(spreadFinancialValue),
      summedValues
    };
  }

  return {
    value: financialValueValue(spreadFinancialValue),
    summedValues: [
      {
        accountId,
        spreadFinancialValue,
        spreadFinancialValueLens
      }
    ]
  };
};

export const reduceAccountToValue = (
  getChildrenById: (id: AccountId) => AccountId[],
  getDescriptionById: (id: AccountId) => AccountDescription,
  getFormulaById: (id: AccountId) => AccountFormula
) => (
  accountValueMap: AccountValueMap,
  { accountId, value, lens }: SpreadFinancialValueNode
) =>
  assoc(
    accountId,
    getAccountValue(
      accountId,
      getDescriptionById(accountId),
      value,
      lens,
      getFormulaById(accountId),
      getChildrenById(accountId),
      accountValueMap
    ),
    accountValueMap
  );

export const buildAccountValues = (
  accountHierarchy: AccountHierarchy,
  accountDescriptions: AccountDescriptions,
  accountFormulas: AccountFormulas,
  spreadFinancialValues: SpreadFinancialValues
): AccountValues =>
  mapReduceSpreadFinancialValues(
    accountHierarchy,
    spreadFinancialValues,
    reduceAccountToValue(
      getChildren(accountHierarchy),
      getAccountDescription(accountDescriptions),
      getAccountFormula(accountFormulas)
    ),
    {}
  );

export const getAccountValuesById = curry(
  (accountValues: AccountValues, accountId: AccountId) =>
    map(
      accountValueMap =>
        accountValueMap[accountId] && accountValueMap[accountId].value,
      accountValues
    )
);

export const getAccountValuesByMultipleIds = curry(
  (accountValues: AccountValues, accountIds: AccountId[]) => {
    const accountMap = accountValues[0];
    const specialKey =
      find((key: string) => Boolean(accountMap[key]))(accountIds) ||
      accountIds[0];
    return getAccountValuesById(accountValues, specialKey);
  }
);

export const getUserEditedStatesByPeriod = curry(
  (spreadFinancialValues: SpreadFinancialValues, accountId: AccountId) => {
    switch (spreadFinancialValues.type) {
      case SpreadFinancialValuesStatus.present: {
        const lenses = map(
          lensMap => lensMap[accountId],
          spreadFinancialValues.lensesByPeriod
        );
        return map(
          lens => view(lens, spreadFinancialValues.spread).userEditedValue,
          lenses
        );
      }
      default:
        return [];
    }
  }
);

export const getSummedValueLensesById = curry(
  (
    accountValues: AccountValues,
    accountId: AccountId
  ): SpreadFinancialValueLens[][] =>
    map(
      accountValueMap =>
        map(
          summedValue => summedValue.spreadFinancialValueLens,
          accountValueMap[accountId].summedValues
        ),
      accountValues
    )
);

export const getSummedValuesById = curry(
  (accountValueMaps: AccountValues, accountId: AccountId): SummedValue[][] =>
    map(
      accountValueMap => accountValueMap[accountId].summedValues,
      accountValueMaps
    )
);



// WEBPACK FOOTER //
// ./src/spread/model/derivedModel/accountValues.ts